
import Vue from "vue";
import {
  DATABASE_SOURCES,
  getCurrentDatabaseSource,
  getOfficeAcquisitionReport
} from "@/api";
import {
  AcquisitionOffice,
  AcquisitionReport
} from "@/interfaces/OfficeAcquisition.interfaces";
import {
  cohortCsv,
  minorMoney,
  officeLabel,
  reportWindow,
  utcLabel
} from "./presentation";

export default Vue.extend({
  name: "OfficeAcquisition",
  data() {
    const now = new Date();
    return {
      from: new Date(now.getTime() - 29 * 86400000).toISOString().slice(0, 10),
      through: now.toISOString().slice(0, 10),
      asOf: now.toISOString().slice(0, 16),
      basis: "completed",
      model: "signupLastNonDirect",
      tab: 0,
      loading: false,
      error: "",
      report: null as AcquisitionReport | null,
      requestId: 0,
      replicaSelected:
        getCurrentDatabaseSource() === DATABASE_SOURCES.PROD_REPLICA,
      bases: [
        { text: "Completed new-customer registrations", value: "completed" },
        { text: "Historical Location-created baseline", value: "legacy" }
      ],
      models: [
        { text: "Signup last non-direct v1", value: "signupLastNonDirect" },
        { text: "First external touch v1", value: "firstExternal" },
        { text: "Pre-signup latest paid v1", value: "latestPaid" }
      ],
      cohortHeaders: [
        { text: "Office", value: "office" },
        { text: "Completed registration", value: "completedOn" },
        { text: "Source / campaign", value: "acquisition.source" },
        { text: "Posted within seven days", value: "postedWithinSevenDays" },
        { text: "First completed shift", value: "firstCompletedOn" },
        { text: "Repeat completed shifts", value: "repeatCompletedJobs" }
      ],
      legacyHeaders: [
        { text: "Office", value: "office" },
        { text: "Location created", value: "createdOn" },
        { text: "Current source", value: "utmSource" },
        { text: "Current campaign", value: "utmCampaign" },
        { text: "Coverage", value: "coverage" }
      ],
      moneyHeaders: [
        { text: "Product", value: "feeType" },
        { text: "Currency", value: "currency" },
        { text: "Movement", value: "kind" },
        { text: "Coverage", value: "coverage" },
        { text: "Gross source cash", value: "grossMinor" },
        {
          text: "Professional pay (excluded from fees)",
          value: "allocatedProfessionalMinor"
        },
        { text: "Allocated fee (uncertified)", value: "allocatedFeeMinor" },
        { text: "Allocated tax", value: "allocatedTaxMinor" },
        { text: "Unallocated", value: "unallocatedMinor" },
        { text: "Source facts", value: "facts" }
      ],
      healthHeaders: [
        { text: "Destination", value: "destination" },
        { text: "State", value: "state" },
        { text: "Facts", value: "facts" },
        { text: "Accepted, not verified", value: "acceptedNotVerified" },
        { text: "Destination mismatches", value: "destinationMismatches" },
        { text: "Oldest occurrence (UTC)", value: "oldestOccurrence" }
      ],
      footerProps: { "items-per-page-options": [10, 25, 50, 100] }
    };
  },
  computed: {
    isLegacy(): boolean {
      return !!this.report && this.report.basis === "legacy_location_created";
    },
    offices(): AcquisitionOffice[] {
      return (this.report && this.report.included) || [];
    },
    excluded(): NonNullable<AcquisitionReport["excluded"]> {
      return (this.report && this.report.excluded) || [];
    },
    legacyRows(): NonNullable<AcquisitionReport["rows"]> {
      return (this.report && this.report.rows) || [];
    },
    moneyRows(): NonNullable<AcquisitionReport["money"]>["rows"] {
      return (this.report && this.report.money && this.report.money.rows) || [];
    },
    moneyBalances(): NonNullable<
      NonNullable<AcquisitionReport["money"]>["balances"]
    > {
      return (
        (this.report && this.report.money && this.report.money.balances) || []
      );
    },
    supportedProducts(): string {
      const products =
        this.report &&
        this.report.money &&
        this.report.money.coverage.supported;
      return products && products.length
        ? products.map(this.feeTypeLabel).join(", ")
        : "Unavailable";
    },
    unavailableProducts(): string {
      const products =
        this.report &&
        this.report.money &&
        this.report.money.coverage.unavailable;
      return products && products.length
        ? products.map((value: string) => this.coverageLabel(value)).join(", ")
        : "Unavailable";
    },
    metrics(): Array<{ label: string; value: string }> {
      const r = this.report;
      if (!r) return [];
      const mature = r.matureSevenDayOffices || 0,
        posted = r.postedWithinSevenDays || 0;
      return [
        {
          label: "Distinct new customers",
          value: this.known(r.eligibleCustomers)
        },
        {
          label: "Acquisition locations",
          value: this.known(r.acquisitionLocations)
        },
        {
          label: "Observed expansion locations",
          value: this.known(r.expansionLocations)
        },
        {
          label: "Seven-day first post (mature offices)",
          value: mature
            ? `${posted} / ${mature} (${((posted / mature) * 100).toFixed(1)}%)`
            : "Not yet mature"
        }
      ];
    }
  },
  mounted() {
    window.addEventListener("database-source-changed", this.databaseChanged);
    this.load();
  },
  beforeDestroy() {
    this.requestId++;
    window.removeEventListener("database-source-changed", this.databaseChanged);
  },
  methods: {
    feeTypeLabel(value: string | undefined): string {
      const labels: Record<string, string> = {
        "placement-fee": "Placement fee",
        boost: "Boost",
        "featured-listing": "Featured listing",
        "job-board-fee": "Job-board fee",
        "token-purchase": "Token purchase",
        "payment-processing-fee": "Payment processing fee",
        source_verified_placement_fee: "Placement fee"
      };
      return value
        ? labels[value] || this.coverageLabel(value)
        : "Product unknown";
    },
    utcLabel,
    minorMoney,
    coverageLabel(value: string | null, fallback = "Unknown"): string {
      if (!value || value === "null") return fallback;
      return value.replace(/_/g, " ");
    },
    known(value: unknown): string {
      return value === null || value === undefined
        ? "Unavailable"
        : String(value);
    },
    label(id: number): string {
      return this.report ? officeLabel(this.report, id) : "Unknown";
    },
    postingLabel(row: AcquisitionOffice): string {
      return !row.sevenDayMature
        ? "Not yet mature"
        : row.postedWithinSevenDays
        ? "Yes"
        : "No";
    },
    cohortMoney(id: string) {
      return ((this.report && this.report.cohortMoney) || []).filter(
        row => row.registrationReceiptId === id
      );
    },
    legacyCoverage(
      row: NonNullable<AcquisitionReport["rows"]>[number]
    ): string {
      return [
        row.isTest ? "Test" : "Test status not flagged",
        row.exclusionReason,
        row.removed || row.deactivated ? "Removed / deactivated" : "Active",
        row.priorLocations ? "Later location" : "First known location"
      ]
        .filter(Boolean)
        .join(" · ");
    },
    invalidate() {
      this.requestId++;
      this.report = null;
      this.error = "";
      this.loading = false;
    },
    databaseChanged() {
      this.invalidate();
      this.replicaSelected =
        getCurrentDatabaseSource() === DATABASE_SOURCES.PROD_REPLICA;
    },
    async load() {
      this.invalidate();
      const requestId = this.requestId;
      if (this.replicaSelected) return;
      try {
        const query = reportWindow(
          this.from,
          this.through,
          this.asOf,
          this.model,
          this.basis
        );
        this.loading = true;
        const report = await getOfficeAcquisitionReport(query);
        if (requestId === this.requestId) this.report = report;
      } catch (error) {
        if (requestId === this.requestId)
          this.error =
            error instanceof Error
              ? error.message
              : "The report could not be loaded. Try again.";
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    download() {
      if (!this.report || this.loading) return;
      const blob = new Blob(["\uFEFF", cohortCsv(this.report)], {
        type: "text/csv;charset=utf-8"
      });
      const url = URL.createObjectURL(blob),
        link = document.createElement("a");
      link.href = url;
      link.download = `office-acquisition-${
        this.report.environment
      }-${this.report.from.slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    }
  }
});
